import {
  Children,
  cloneElement,
  type ComponentProps,
  isValidElement,
  type ReactNode,
} from "react";
import { Page } from "react-pdf";
import { type PDFPageProxy } from "pdfjs-dist";

import { PDFPageErrorBoundary } from "./PDFPageErrorBoundary";

export type PdfCustomTextRenderer = ComponentProps<
  typeof Page
>["customTextRenderer"];

type PDFViewerPageProps = {
  pageNumber: number;
  pageIndex: number;
  scale: number;
  children?: ReactNode;
  className?: string;
  renderAnnotationLayer?: boolean;
  customTextRenderer?: PdfCustomTextRenderer;
  resetKey?: string;
  onMouseEnter?: () => void;
  onLoadSuccess?: (page: PDFPageProxy) => void;
  onRenderSuccess?: () => void;
};

type PageProps = {
  pageNumber: number;
  pageIndex: number;
};

const injectPageProps = (
  children: ReactNode,
  pageProps: PageProps,
): ReactNode => {
  if (!children) {
    return children;
  }

  return Children.map(children, (child) => {
    if (!isValidElement(child)) {
      return child;
    }

    return cloneElement(child, pageProps);
  });
};

export const PDFViewerPage = ({
  pageNumber,
  pageIndex,
  scale,
  children,
  className,
  renderAnnotationLayer = true,
  customTextRenderer,
  resetKey,
  onMouseEnter,
  onLoadSuccess,
  onRenderSuccess,
}: PDFViewerPageProps) => (
  <PDFPageErrorBoundary resetKey={resetKey}>
    <Page
      className={className}
      customTextRenderer={customTextRenderer}
      loading=""
      onLoadSuccess={onLoadSuccess}
      onMouseEnter={onMouseEnter}
      onRenderSuccess={onRenderSuccess}
      pageNumber={pageNumber}
      renderAnnotationLayer={renderAnnotationLayer}
      scale={scale}
    >
      {injectPageProps(children, { pageNumber, pageIndex })}
    </Page>
  </PDFPageErrorBoundary>
);
